import { useEffect, useState } from "react";
import ModalComponent from "../../common/ModalComponent";
import { createAgentAPI, getAgentsAPI, getRAGCorpusAPI } from "../../../service/ai-model.service";
import { useAppContext } from "../../../context/app.context";
import { ToastType } from "../../../helper/constant";
import CustomLoader from "../../common/CustomLoader";
import { Form } from "react-bootstrap";
import CustomDropdown from "../../common/CustomDropdown";
import { set } from "firebase/database";

export const GENERATIVE_AGENT_TYPE = {
    RESEARCH: 'RESEARCH',
    RAG: 'RAG',
    REGULAR: 'REGULAR'
}

const AddAgentDialog = ({ showModal, setShowModal, title }) => {

    const [agentName, setAgentName] = useState('');
    const [agentDescription, setAgentDescription] = useState('');
    const [searchTool, setSearchTool] = useState(false);
    const [ragTool, setRagTool] = useState(false);
    const [agentPrompt, setAgentPrompt] = useState('');
    const { showAlert } = useAppContext();
    const [loading, setLoading] = useState(false);
    const [existingAgents, setExistingAgents] = useState([]);
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [existingRagCorpus, setExistingRagCorpus] = useState([]);
    const [selectedRagCorpus, setSelectedRagCorpus] = useState(null);

    useEffect(() => {
        if (showModal) {
            setAgentName('');
            setAgentDescription('');
            setAgentPrompt('');
            getExistingAgents();
            getExistingRagCorpus();
            setSelectedAgent(null);
            setSelectedRagCorpus(null);
            setSearchTool(false);
            setRagTool(false);
        }
    }, [showModal]);

    useEffect(() => {
        if (selectedAgent) {
            const agent = existingAgents.find((agent) => agent.id === selectedAgent);
            if (agent) {
                setAgentName(agent.agent_name);
                setAgentPrompt(agent.agent_prompt || '');
                setAgentDescription(agent.agent_description || '');
                setSearchTool(agent.agent_type === GENERATIVE_AGENT_TYPE.RESEARCH);
                setRagTool(agent.agent_type === GENERATIVE_AGENT_TYPE.RAG);
                setSelectedRagCorpus(agent.rag_corpus || null);
            }
        }
    }, [selectedAgent]);

    const getExistingAgents = () => {
        getAgentsAPI().then((response) => {
            if (response?.data?.length) {
                setExistingAgents(response.data);
            }
        }).catch((error) => {
            showAlert("Failed to fetch existing agents.", ToastType.ERROR);
        });
    }

    const onAddAgentSubmit = (e) => {
        e.preventDefault();
        if (!agentName || !agentPrompt || (ragTool && !selectedRagCorpus)) {
            showAlert("Please fill in all required fields.", ToastType.ERROR);
            return;
        }
        setLoading(true);
        const payload = {
            name: agentName,
            description: agentDescription,
            prompt: agentPrompt,
            type: !searchTool && !ragTool ? GENERATIVE_AGENT_TYPE.REGULAR : searchTool ? GENERATIVE_AGENT_TYPE.RESEARCH : GENERATIVE_AGENT_TYPE.RAG,
            ragCorpus: selectedRagCorpus
        };
        createAgentAPI(payload, selectedAgent)
            .then((response) => {
                if (response) {
                    showAlert("Agent saved !", ToastType.SUCCESS);
                } else {
                    showAlert("Failed to save agent", ToastType.ERROR);
                }
            })
            .catch((error) => showAlert(error.message || "Failed to save agent", ToastType.ERROR))
            .finally(() => { setLoading(false) });
    }

    const getExistingRagCorpus = () => {
        getRAGCorpusAPI().then((response) => {
            if (response?.corpora?.length) {
                setExistingRagCorpus(response.corpora);
            }
        }).catch((error) => {
            showAlert("Failed to fetch existing Corpus.", ToastType.ERROR);
        });
    }


    const renderExistingCorpus = () => {
        if (!existingRagCorpus.length) {
            return null;
        }
        return (
            <>
                <div className="d-flex flex-column mb-2 gap-0">
                    <div className="font-weight-bold mb-2">Existing RAG Corpus <span className="text-danger">*</span></div>
                    <div className="w-100">
                        <CustomDropdown
                            name=""
                            options={existingRagCorpus}
                            field="name"
                            value={selectedRagCorpus}
                            onChange={(e) => setSelectedRagCorpus(e)}
                        />
                    </div>
                </div>
            </>
        );
    };

    const renderAddAgent = () => {
        if (loading) {
            return (<div className="d-flex justify-content-center"><CustomLoader /></div>);
        }
        return (
            <button type="submit" className="btn btn-primary">Save</button>
        )
    }

    const renderExistingAgents = () => {
        if (!existingAgents.length) {
            return null;
        }
        return (
            <>
                <div className="d-flex align-items-center mb-2 gap-2">
                    <div className="font-weight-bold mb-2">Existing Agents</div>
                    <div className="w-30">
                        <CustomDropdown
                            showResearch
                            name=""
                            options={existingAgents}
                            field="agent_name"
                            value={selectedAgent}
                            onChange={(e) => setSelectedAgent(e)}
                        />
                    </div>
                    <button type="button" className="btn2 border-0" onClick={() => {
                        setAgentName(agentName + '_copy');
                        setAgentDescription(agentDescription);
                        setAgentPrompt(agentPrompt);
                        setSelectedAgent(null);
                    }}>Duplicate</button>
                    <button type="button" className="btn2 border-0" onClick={() => {
                        setSelectedAgent(null);
                        setAgentName('');
                        setAgentPrompt('');
                        setAgentDescription('');
                    }} >Add New</button>
                </div>
            </>
        );
    };

    const renderAddAgentForm = () => {
        return (
            <div className="d-flex flex-column">
                {renderExistingAgents()}
                <p>Please fill in the agent details below:</p>
                <form className="d-flex flex-column gap-3" onSubmit={onAddAgentSubmit} noValidate>
                    <div>
                        <div className="h6 f-13">
                            <b>Agent Name</b> <span className="text-danger">*</span>
                        </div>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Agent Name"
                            value={agentName}
                            pattern="[0-9]*"
                            onChange={(e) => setAgentName(e.target.value)}
                            name="agentName"
                            readOnly={selectedAgent !== null}
                        />
                    </div>
                    <div>
                        <div className="h6 f-13">
                            <b>Agent Prompt</b> <span className="text-danger">*</span>
                        </div>
                        <textarea
                            type="text"
                            row={2}
                            className="form-control"
                            placeholder="Agent Prompt"
                            value={agentPrompt}
                            onChange={(e) => setAgentPrompt(e.target.value)}
                            name="agentPrompt"
                        />
                    </div>
                    {ragTool && <div>{renderExistingCorpus()}</div>}
                    {renderTools()}
                    {renderAddAgent()}
                </form>
            </div >
        );
    }

    const renderTools = () => {
        return (
            <div>
                <div className="h6 f-13"><b>Agent Tools</b></div>
                <Form.Check
                    type="checkbox"
                    id='searchTool'
                    label="Use Deep Research"
                    value={searchTool}
                    onChange={() => setSearchTool(!searchTool)}
                />
                <Form.Check
                    type="checkbox"
                    id='ragTool'
                    label="Use RAG Corpus"
                    value={ragTool}
                    onChange={() => setRagTool(!ragTool)}
                />
            </div>)
    }

    const render = () => {
        return (
            <ModalComponent
                full
                title="Manage Agent"
                show={showModal}
                handleClose={setShowModal}
                classes={'w-50'}
            >
                {renderAddAgentForm()}
            </ModalComponent>
        )
    }
    return render();
}

export default AddAgentDialog;